import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as Dialog from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, X, ArrowRight, ArrowLeft, Loader2, CheckCircle2, ChevronDown,
  Package as PackageIcon, ExternalLink, User, Mail, Phone, Building2, Briefcase,
} from "lucide-react";
import { api } from "../lib/api";

const SESSION_KEY = "kb_onboarding_seen";
const OPEN_DELAY_MS = 1400;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const initialForm = { name: "", email: "", phone: "", organisation: "", businessType: "", description: "" };

export function OnboardingDialog() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intake"); // intake | analyzing | results | done
  const [form, setForm] = useState(initialForm);
  const [analysis, setAnalysis] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [expandedIndex, setExpandedIndex] = useState(-1);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (sessionStorage.getItem(SESSION_KEY)) return;
    const t = setTimeout(() => {
      setOpen(true);
      sessionStorage.setItem(SESSION_KEY, "1");
    }, OPEN_DELAY_MS);
    return () => clearTimeout(t);
  }, []);

  // Belt-and-braces: keep the page behind from scrolling while the dialog is open,
  // regardless of pointer position — only the dialog card itself should scroll.
  useEffect(() => {
    if (!open) return;
    const html = document.documentElement;
    const originalBody = document.body.style.overflow;
    const originalHtml = html.style.overflow;
    document.body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    window.__kbLenis?.stop();
    return () => {
      document.body.style.overflow = originalBody;
      html.style.overflow = originalHtml;
      window.__kbLenis?.start();
    };
  }, [open]);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const analyze = async () => {
    setError("");
    setStep("analyzing");
    try {
      const { data } = await api.post("/onboarding/analyze", {
        name: form.name,
        organisation: form.organisation,
        businessType: form.businessType,
        description: form.description,
      });
      setAnalysis(data);
      setSelectedIndex(0);
      setExpandedIndex(-1);
      setStep("results");
    } catch (err) {
      setError("Something went wrong while analysing your business. Please try again.");
      setStep("intake");
    }
  };

  const submitPackage = async () => {
    if (submitting) return;
    setSubmitting(true);
    setError("");
    const pkg = analysis?.packages?.[selectedIndex];
    try {
      await api.post("/leads", {
        name: form.name,
        email: form.email,
        phone: form.phone,
        company: form.organisation,
        service: pkg?.title || "General Enquiry",
        message: `Business type: ${form.businessType || "N/A"}. ${form.description || ""}`.trim(),
        source: "onboarding_dialog",
        businessType: form.businessType,
        packageTitle: pkg?.title || "",
        package: pkg || null,
        aiNote: analysis?.note || "",
        aiSource: analysis?.source || "",
        packagesOffered: analysis?.packages || [],
      });
      setStep("done");
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => setOpen(false);

  const resetAndClose = () => {
    setOpen(false);
    setTimeout(() => {
      setStep("intake");
      setForm(initialForm);
      setAnalysis(null);
      setSelectedIndex(0);
      setExpandedIndex(-1);
      setError("");
    }, 300);
  };

  return (
    <Dialog.Root open={open} onOpenChange={(v) => (v ? setOpen(true) : close())}>
      <AnimatePresence>
        {open ? (
          <Dialog.Portal forceMount>
            <Dialog.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/75 backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              />
            </Dialog.Overlay>
            <Dialog.Content asChild forceMount aria-describedby="onboarding-desc">
              <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-4">
                <motion.div
                  className="w-full max-w-2xl max-h-[92dvh] overflow-y-auto overscroll-contain kb-glass rounded-2xl bg-[#0a1330] shadow-2xl relative"
                  initial={{ opacity: 0, y: 24, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 16, scale: 0.97 }}
                  transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  data-testid="onboarding-dialog"
                >
                  <Dialog.Close
                    onClick={resetAndClose}
                    className="absolute right-4 top-4 sm:right-5 sm:top-5 z-10 text-white/50 hover:text-[#d4a840] transition-colors"
                    data-testid="onboarding-close"
                  >
                    <X size={18} />
                    <span className="sr-only">Close</span>
                  </Dialog.Close>

                  <div className="p-5 sm:p-7">
                    <AnimatePresence mode="wait">
                      {step === "intake" && (
                        <motion.div key="intake" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                          <IntakeWizard form={form} onChange={onChange} onSubmit={analyze} error={error} />
                        </motion.div>
                      )}
                      {step === "analyzing" && (
                        <motion.div key="analyzing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                          <AnalyzingStep />
                        </motion.div>
                      )}
                      {step === "results" && analysis && (
                        <motion.div key="results" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} transition={{ duration: 0.2 }}>
                          <ResultsStep
                            analysis={analysis}
                            selectedIndex={selectedIndex}
                            setSelectedIndex={setSelectedIndex}
                            expandedIndex={expandedIndex}
                            setExpandedIndex={setExpandedIndex}
                            onSubmit={submitPackage}
                            submitting={submitting}
                            error={error}
                          />
                        </motion.div>
                      )}
                      {step === "done" && (
                        <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
                          <DoneStep email={form.email} onClose={resetAndClose} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        ) : null}
      </AnimatePresence>
    </Dialog.Root>
  );
}

const WIZARD_FIELDS = [
  { key: "name", icon: User, title: "What's your name?", subtitle: "So we know who we're talking to.", type: "text", placeholder: "Full name", required: true },
  { key: "email", icon: Mail, title: "What's your email?", subtitle: "We'll send your package details here.", type: "email", placeholder: "you@company.com", required: true },
  { key: "phone", icon: Phone, title: "Best number to reach you?", subtitle: "Optional — helps us follow up faster.", type: "text", placeholder: "+91 ...", required: false },
  { key: "organisation", icon: Building2, title: "What's your business called?", subtitle: "So we know who we're building this for.", type: "text", placeholder: "Business / company name", required: true },
];

function isFieldValid(field, value) {
  const v = (value || "").trim();
  if (field.key === "email") return EMAIL_RE.test(v);
  if (field.required) return v.length >= 2;
  return true;
}

function IntakeWizard({ form, onChange, onSubmit, error }) {
  const [sub, setSub] = useState(0);
  const totalSteps = WIZARD_FIELDS.length + 1;
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, [sub]);

  const isLast = sub === WIZARD_FIELDS.length;
  const field = WIZARD_FIELDS[sub];

  const currentValid = isLast
    ? form.businessType.trim().length > 0
    : isFieldValid(field, form[field.key]);

  const goNext = (e) => {
    e.preventDefault();
    if (!currentValid) return;
    if (isLast) {
      onSubmit();
    } else {
      setSub((s) => s + 1);
    }
  };

  const goBack = () => setSub((s) => Math.max(0, s - 1));

  return (
    <>
      <div className="flex items-center gap-1.5 mb-5" data-testid="onboarding-progress">
        {Array.from({ length: totalSteps }).map((_, i) => (
          <div
            key={i}
            className={`h-1.5 rounded-full transition-all duration-300 ${
              i === sub ? "w-6 bg-[#d4a840]" : i < sub ? "w-1.5 bg-[#d4a840]/60" : "w-1.5 bg-white/15"
            }`}
          />
        ))}
      </div>

      <form onSubmit={goNext} data-testid="onboarding-intake-form">
        <AnimatePresence mode="wait">
          {!isLast ? (
            <motion.div
              key={field.key}
              initial={{ opacity: 0, x: 12 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -12 }}
              transition={{ duration: 0.2 }}
            >
              <div className="h-12 w-12 rounded-2xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-4">
                <field.icon size={22} className="text-[#d4a840]" />
              </div>
              <div className="kb-label mb-2">{field.required ? "Step" : "Step (optional)"} {sub + 1} of {totalSteps}</div>
              <Dialog.Title className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                {field.title}
              </Dialog.Title>
              {sub === 0 ? (
                <Dialog.Description id="onboarding-desc" className="mt-2 text-sm text-white/60 leading-relaxed">
                  We'll instantly suggest the registrations and licences your business actually needs — no obligation.
                </Dialog.Description>
              ) : (
                <p className="mt-2 text-sm text-white/50">{field.subtitle}</p>
              )}

              <input
                ref={inputRef}
                type={field.type}
                name={field.key}
                value={form[field.key]}
                onChange={onChange}
                data-testid={`onboarding-field-${field.key}`}
                className="mt-6 w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3.5 text-lg text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
                placeholder={field.placeholder}
              />
            </motion.div>
          ) : (
            <motion.div key="business" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} transition={{ duration: 0.2 }}>
              <div className="h-12 w-12 rounded-2xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-4">
                <Briefcase size={22} className="text-[#d4a840]" />
              </div>
              <div className="kb-label mb-2">Step {totalSteps} of {totalSteps}</div>
              <Dialog.Title className="font-display text-2xl sm:text-3xl font-bold text-white leading-snug">
                What kind of business is it?
              </Dialog.Title>
              <p className="mt-2 text-sm text-white/50">This is what our AI uses to build your package.</p>

              <input
                ref={inputRef}
                type="text"
                name="businessType"
                value={form.businessType}
                onChange={onChange}
                data-testid="onboarding-field-businesstype"
                className="mt-6 w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3.5 text-lg text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors"
                placeholder="e.g. Restaurant, Manufacturing unit, Retail store, IT consultancy..."
              />
              <textarea
                name="description"
                value={form.description}
                onChange={onChange}
                rows={2}
                data-testid="onboarding-field-description"
                className="mt-3 w-full bg-[#0a1f4d]/60 border border-[#d4a840]/20 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 focus:border-[#d4a840] focus:outline-none transition-colors resize-none"
                placeholder="Anything else we should know? (optional)"
              />
            </motion.div>
          )}
        </AnimatePresence>

        {error ? <p className="text-sm text-red-400 mt-4">{error}</p> : null}

        <div className="flex items-center gap-3 mt-6">
          {sub > 0 ? (
            <button type="button" onClick={goBack} data-testid="onboarding-back" className="kb-btn-ghost !px-4">
              <ArrowLeft size={16} />
            </button>
          ) : null}
          <button
            type="submit"
            disabled={!currentValid}
            data-testid={isLast ? "onboarding-analyze-submit" : "onboarding-next"}
            className="kb-btn-primary flex-1 justify-center disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {isLast ? <>Get my compliance package <Sparkles size={16} /></> : <>Continue <ArrowRight size={16} /></>}
          </button>
        </div>
      </form>
    </>
  );
}

function AnalyzingStep() {
  return (
    <div className="py-12 sm:py-16 flex flex-col items-center text-center" data-testid="onboarding-analyzing">
      <Dialog.Title className="sr-only">Analysing your business</Dialog.Title>
      <Dialog.Description id="onboarding-desc" className="sr-only">Please wait while we analyse your business.</Dialog.Description>
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }}
        className="h-16 w-16 rounded-full border-2 border-[#d4a840]/20 border-t-[#d4a840] mb-6"
      />
      <h3 className="font-display text-xl font-semibold text-white">Analysing your business...</h3>
      <p className="mt-2 text-white/50 text-sm max-w-xs">Matching you against our real service catalog to find what actually applies.</p>
    </div>
  );
}

function ResultsStep({ analysis, selectedIndex, setSelectedIndex, expandedIndex, setExpandedIndex, onSubmit, submitting, error }) {
  const packages = analysis.packages || [];

  return (
    <>
      <div className="h-9 w-9 sm:h-10 sm:w-10 rounded-xl bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-3">
        <CheckCircle2 size={18} className="text-[#d4a840]" />
      </div>
      <div className="kb-label mb-1.5">AI Recommendation</div>
      <Dialog.Title className="font-display text-xl sm:text-2xl font-bold text-white leading-snug">
        Here's what your business needs
      </Dialog.Title>
      <Dialog.Description id="onboarding-desc" className="mt-1.5 text-sm text-white/70 leading-relaxed">
        {analysis.note}
      </Dialog.Description>

      <div className="mt-4 space-y-2" data-testid="onboarding-package-list">
        {packages.map((pkg, i) => {
          const isRecommended = i === 0;
          const isSelected = i === selectedIndex;
          const isExpanded = i === expandedIndex;
          return (
            <div
              key={`${pkg.title}-${i}`}
              className={`rounded-xl border transition-colors ${
                isSelected ? "border-[#d4a840] bg-[#d4a840]/[0.06]" : "border-white/10 bg-white/[0.02] hover:border-[#d4a840]/30"
              }`}
              data-testid={`onboarding-package-${i}`}
            >
              <button
                type="button"
                onClick={() => {
                  setSelectedIndex(i);
                  setExpandedIndex(isExpanded ? -1 : i);
                }}
                className="w-full flex items-center gap-3 px-4 py-2.5 sm:py-3 text-left"
              >
                <div className="h-8 w-8 sm:h-9 sm:w-9 rounded-lg bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center shrink-0">
                  <PackageIcon size={16} className="text-[#d4a840]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-white">{pkg.title}</span>
                    {isRecommended ? (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-[#050A15] bg-[#d4a840] rounded-full px-2 py-0.5">
                        AI Pick
                      </span>
                    ) : null}
                  </div>
                  <div className="text-xs text-white/50 mt-0.5">{pkg.certificates.length} certificates included</div>
                </div>
                <ChevronDown size={16} className={`text-white/40 shrink-0 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
              </button>

              <AnimatePresence initial={false}>
                {isExpanded ? (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <ul className="px-4 pb-4 pt-1 space-y-2 border-t border-white/10 mt-1">
                      {pkg.certificates.map((cert) => (
                        <li key={cert.title} className="flex items-center justify-between gap-3 text-xs text-white/70 pt-2">
                          <span>{cert.title}</span>
                          <Link
                            to={`/services/${cert.serviceSlug}`}
                            target="_blank"
                            className="text-[#d4a840]/80 hover:text-[#d4a840] shrink-0 flex items-center gap-1"
                          >
                            Details <ExternalLink size={11} />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </div>
          );
        })}
      </div>

      {error ? <p className="text-sm text-red-400 mt-4">{error}</p> : null}

      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        data-testid="onboarding-select-package-submit"
        className="kb-btn-primary w-full justify-center mt-4 disabled:opacity-70 disabled:cursor-not-allowed"
      >
        {submitting ? <><Loader2 size={18} className="animate-spin" /> Sending...</> : <>Get this package — contact me <ArrowRight size={16} /></>}
      </button>
    </>
  );
}

function DoneStep({ email, onClose }) {
  return (
    <div className="py-10 sm:py-14 flex flex-col items-center text-center" data-testid="onboarding-done">
      <Dialog.Title className="sr-only">Request sent</Dialog.Title>
      <Dialog.Description id="onboarding-desc" className="sr-only">Your request has been sent successfully.</Dialog.Description>
      <div className="h-16 w-16 rounded-full bg-[#d4a840]/10 border border-[#d4a840]/30 flex items-center justify-center mb-6">
        <CheckCircle2 size={30} className="text-[#d4a840]" />
      </div>
      <h3 className="font-display text-2xl font-semibold text-white">Thanks — we've got it!</h3>
      <p className="mt-3 text-white/60 text-sm max-w-sm leading-relaxed">
        {email
          ? `We've sent a confirmation to ${email}. Our team will reach out within 24 hours with your package details and next steps.`
          : "Our team will reach out within 24 hours with your package details and next steps."}
      </p>
      <button type="button" onClick={onClose} className="kb-btn-ghost mt-8">
        Continue exploring
      </button>
    </div>
  );
}
